import { ScanFace, Wand2, Compass, ShoppingBag, ArrowUpRight } from "lucide-react";
import { Reveal, Overline } from "./Reveal";
import { Anchor } from "./Navbar";

const WARDROBE_IMG =
    "https://images.unsplash.com/photo-1567401893414-76b7b1e5a7a5?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2Mzl8MHwxfHNlYXJjaHwzfHxmYXNoaW9uJTIwd2FyZHJvYmUlMjBjbG90aGVzfGVufDB8fHx8MTc4Njg3MTgxOXww&ixlib=rb-4.1.0&q=85";

const FEATURES = [
    {
        id: "ai-analysis",
        icon: ScanFace,
        title: "AI Appearance Analysis",
        body: "Upload a photo and let LookAura analyze your appearance, style, and visual characteristics using AI.",
        tall: true,
    },
    {
        id: "personalized",
        icon: Wand2,
        title: "Personalized Recommendations",
        body: "Receive recommendations tailored to your appearance, preferences, and personal style.",
    },
    {
        id: "discover-style",
        icon: Compass,
        title: "Discover Your Style",
        body: "Explore clothing, accessories, hairstyles, and other appearance-related recommendations designed around you.",
    },
    {
        id: "smart-shopping",
        icon: ShoppingBag,
        title: "Smart Shopping Recommendations",
        body: "Discover relevant products from participating online retailers based on your personalized recommendations.",
    },
];

const Card = ({ f, index }) => (
    <Reveal delay={index * 0.1} className={f.tall ? "lg:row-span-2" : ""}>
        <article
            data-testid={`feature-card-${f.id}`}
            className="group h-full bg-[#0A0A0A] border border-white/5 p-8 lg:p-10 flex flex-col justify-between transition-all duration-500 hover:-translate-y-1 hover:border-amber-600/40 hover:shadow-[0_20px_60px_-20px_rgba(217,119,6,0.25)]"
        >
            {f.tall && (
                <div className="overflow-hidden mb-8 border border-white/10">
                    <img
                        src={WARDROBE_IMG}
                        alt="Curated premium garments on a clothing rack, the kind of wardrobe LookAura helps you build"
                        className="w-full h-56 object-cover transition-transform duration-700 group-hover:scale-105"
                        loading="lazy"
                    />
                </div>
            )}
            <div>
                <f.icon className="w-8 h-8 text-amber-500 mb-6" strokeWidth={1.25} />
                <h3 className="font-serif-display text-2xl sm:text-3xl tracking-tight text-[#FAFAFA] mb-4">{f.title}</h3>
                <p className="text-zinc-400 leading-relaxed text-sm sm:text-base">{f.body}</p>
            </div>
        </article>
    </Reveal>
);

export default function Features() {
    return (
        <section id="features" data-testid="features-section" className="relative py-24 sm:py-32">
            <div className="max-w-7xl mx-auto px-6 lg:px-8">
                <Reveal>
                    <Overline data-testid="features-overline">Capabilities</Overline>
                    <div className="mt-6 flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6">
                        <h2 className="font-serif-display text-4xl sm:text-5xl tracking-tight text-[#FAFAFA] max-w-xl">
                            Intelligence, <span className="italic text-amber-500">tailored</span> to you
                        </h2>
                        <p className="text-zinc-400 max-w-md text-sm sm:text-base leading-relaxed">
                            LookAura studies your appearance and preferences, then turns that understanding into recommendations you can actually wear.
                        </p>
                    </div>
                    <div className="hairline mt-10" />
                </Reveal>

                <div className="mt-14 grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                    <Card f={FEATURES[0]} index={0} />
                    <Card f={FEATURES[1]} index={1} />
                    <Card f={FEATURES[2]} index={2} />
                    <div className="md:col-span-2 lg:col-span-2">
                        <Card f={FEATURES[3]} index={3} />
                    </div>
                </div>

                <Reveal delay={0.2} className="mt-10">
                    <Anchor hash="#how-it-works" testId="features-how-it-works-link" className="group inline-flex items-center gap-2 text-sm text-amber-400 hover:text-amber-300 transition-colors">
                        See how it works
                        <ArrowUpRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                    </Anchor>
                </Reveal>
            </div>
        </section>
    );
}
